import { lazy, Suspense, type ComponentType } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { CollabLayout } from "@frontend/layouts/collab-layout";
import { CollabsPage } from "@frontend/pages/collabs";
import { CollabRoomPage } from "@frontend/pages/collab-room";
import { ContractPage } from "@frontend/pages/contract";

function lazyNamed<TModule, TKey extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[exportName] as ComponentType };
  });
}

const SiteLayout = lazyNamed(() => import("@frontend/layouts/site-layout"), "SiteLayout");
const AuthLayout = lazyNamed(() => import("@frontend/layouts/auth-layout"), "AuthLayout");
const WorkspaceLayout = lazyNamed(
  () => import("@frontend/layouts/workspace-layout"),
  "WorkspaceLayout",
);
const CreatorLayout = lazyNamed(() => import("@frontend/layouts/creator-layout"), "CreatorLayout");
const SettingsLayout = lazyNamed(
  () => import("@frontend/layouts/settings-layout"),
  "SettingsLayout",
);

const HomePage = lazyNamed(() => import("@frontend/pages/home"), "HomePage");
const LoginPage = lazyNamed(() => import("@frontend/pages/login"), "LoginPage");
const SignUpPage = lazyNamed(() => import("@frontend/pages/signup"), "SignUpPage");
const FeaturesPage = lazyNamed(() => import("@frontend/pages/features"), "FeaturesPage");
const FaqPage = lazyNamed(() => import("@frontend/pages/faq"), "FaqPage");
const HowItWorksPage = lazyNamed(() => import("@frontend/pages/how-it-works"), "HowItWorksPage");
const OnboardingPage = lazyNamed(() => import("@frontend/pages/onboarding"), "OnboardingPage");
const OverviewPage = lazyNamed(() => import("@frontend/pages/overview"), "OverviewPage");
const WorkHistoryPage = lazyNamed(() => import("@frontend/pages/work-history"), "WorkHistoryPage");
const CreatorPage = lazyNamed(() => import("@frontend/pages/creator"), "CreatorPage");
const ProfileSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/profile"),
  "ProfileSettingsPage",
);
const AccountSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/account"),
  "AccountSettingsPage",
);
const SessionsSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/sessions"),
  "SessionsSettingsPage",
);
const PaymentSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/payments"),
  "PaymentSettingsPage",
);
const TeamSettingsPage = lazyNamed(
  () => import("@frontend/pages/settings/team"),
  "TeamSettingsPage",
);
const NotFoundPage = lazyNamed(() => import("@frontend/pages/not-found"), "NotFoundPage");

export function App() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route index element={<HomePage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="signup" element={<SignUpPage />} />
          <Route path="features" element={<FeaturesPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="how-it-works" element={<HowItWorksPage />} />
        </Route>

        <Route element={<AuthLayout />}>
          <Route path="onboarding" element={<OnboardingPage />} />
        </Route>

        <Route element={<WorkspaceLayout />}>
          <Route path="overview" element={<OverviewPage />} />
          <Route path="dashboard" element={<Navigate to="/overview" replace />} />
          <Route path="work-history" element={<WorkHistoryPage />} />
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="profile" replace />} />
            <Route path="profile" element={<ProfileSettingsPage />} />
            <Route path="account" element={<AccountSettingsPage />} />
            <Route path="sessions" element={<SessionsSettingsPage />} />
            <Route path="payments" element={<PaymentSettingsPage />} />
            <Route path="team" element={<TeamSettingsPage />} />
          </Route>
        </Route>

        <Route element={<CollabLayout />}>
          <Route path="collabs" element={<CollabsPage />} />
          <Route path="collabs/:id" element={<CollabRoomPage />} />
          <Route path="collabs/:id/contract" element={<ContractPage />} />
        </Route>

        <Route path="b/collabs" element={<Navigate to="/collabs" replace />} />
        <Route path="b/deals" element={<Navigate to="/collabs" replace />} />
        <Route path="deals" element={<Navigate to="/collabs" replace />} />
        <Route path="deals/:id" element={<DealsRedirect />} />
        <Route path="deals/:id/contract" element={<DealsContractRedirect />} />

        <Route element={<CreatorLayout />}>
          <Route path=":username" element={<CreatorPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function DealsRedirect() {
  const { id } = useParams();
  return <Navigate to={`/collabs/${id ?? ""}`} replace />;
}

function DealsContractRedirect() {
  const { id } = useParams();
  return <Navigate to={`/collabs/${id ?? ""}/contract`} replace />;
}
